/**
 * @fileoverview React Router 7 configuration.
 * @module app/router
 * @description
 * React port of `angular/src/app/app.routes.ts`. Reproduces the Angular route
 * table exactly (paths, lazy-loaded components, per-route document
 * title/description, and guards). Each screen component is lazy-loaded; guards
 * are React wrapper components (see `./guards/`) that render `<Outlet/>` when
 * access is allowed or `<Navigate replace/>` when not.
 *
 * Route -> guard mapping (matches Angular `canActivate`):
 *   ''                   -> RootRedirect            (redirectGuard)
 *   login                -> RedirectIfAuthed        (loginGuard)
 *   registration         -> RedirectIfAuthed        (loginGuard)
 *   auth/reset-password  -> (none)
 *   facial-recognition   -> RequireAuth             (authGuard)
 *   role-selection       -> RequireAuth             (authGuard)
 *   dashboard            -> RequireAuth + RequireConnection (authGuard, connectionGuard)
 *   service-outage       -> (none)
 *   dev/resilience-test  -> (none)
 *   404 / 500            -> (none)
 *   *                    -> redirect to /404
 *
 * @author Liraz Amir
 */

import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useNavigation,
  type RouteObject,
} from 'react-router-dom';

import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useNavigationLoadingStore } from '@/stores/navigation-loading.store';

import { RootRedirect } from './guards/RootRedirect';
import { RedirectIfAuthed } from './guards/RedirectIfAuthed';
import { RequireAuth } from './guards/RequireAuth';
import { RequireConnection } from './guards/RequireConnection';
import { GuardLoading } from './guards/GuardLoading';

// Lazy-loaded screen components (implemented; each resolves to a real screen).
const LoginRegister = lazy(() =>
  import('@/components/common/auth/login-register/LoginRegister').then((m) => ({
    default: m.LoginRegister,
  })),
);
const Registration = lazy(() =>
  import('@/components/common/auth/registration/Registration').then((m) => ({
    default: m.Registration,
  })),
);
const ResetPassword = lazy(() =>
  import('@/components/common/auth/reset-password/ResetPassword').then((m) => ({
    default: m.ResetPassword,
  })),
);
const FacialRecognition = lazy(() =>
  import('@/components/common/facial-recognition/FacialRecognition').then((m) => ({
    default: m.FacialRecognition,
  })),
);
const RoleSelection = lazy(() =>
  import('@/components/common/role-selection/RoleSelection').then((m) => ({
    default: m.RoleSelection,
  })),
);
const Dashboard = lazy(() =>
  import('@/components/physician/dashboard/Dashboard').then((m) => ({
    default: m.Dashboard,
  })),
);
const ServiceOutagePage = lazy(() =>
  import('@/components/resilience/service-outage-page/ServiceOutagePage').then((m) => ({
    default: m.ServiceOutagePage,
  })),
);
const ResilienceTestPage = lazy(() =>
  import('@/components/resilience/resilience-test-page/ResilienceTestPage').then((m) => ({
    default: m.ResilienceTestPage,
  })),
);
const SimpleNotFound = lazy(() =>
  import('@/components/common/simple-not-found/SimpleNotFound').then((m) => ({
    default: m.SimpleNotFound,
  })),
);
const SimpleServerError = lazy(() =>
  import('@/components/common/simple-server-error/SimpleServerError').then((m) => ({
    default: m.SimpleServerError,
  })),
);

/**
 * Applies the Angular route `data.title` / `data.description` (via
 * `useDocumentTitle`) and lazy-loads the screen under a `<Suspense>` boundary.
 */
function TitledScreen({
  title,
  description,
  Component,
}: {
  title?: string;
  description?: string;
  Component: ComponentType;
}): ReactNode {
  useDocumentTitle(title, description);
  return (
    <Suspense fallback={<GuardLoading />}>
      <Component />
    </Suspense>
  );
}

/**
 * Root layout: wires React Router's navigation lifecycle into the
 * navigation-loading store (Angular subscribed to the Router event stream).
 * `navigation.state === 'loading'` covers lazy-chunk loads / transitions; the
 * guards themselves render their own loading state for their async waits.
 */
function RootLayout() {
  const navigation = useNavigation();
  const navigationStarted = useNavigationLoadingStore((s) => s.navigationStarted);
  const navigationEnded = useNavigationLoadingStore((s) => s.navigationEnded);

  useEffect(() => {
    if (navigation.state === 'idle') {
      navigationEnded();
    } else {
      navigationStarted();
    }
  }, [navigation.state, navigationStarted, navigationEnded]);

  return <Outlet />;
}

/**
 * The full route tree (paths, guards, lazy screens, titles). Exported so tests
 * can mount the exact same routes under a `createMemoryRouter` without the
 * browser history that `createBrowserRouter` owns.
 */
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      // Dynamic redirect based on user state (redirectGuard).
      { index: true, element: <RootRedirect /> },

      // Public auth screens (loginGuard).
      {
        element: <RedirectIfAuthed />,
        children: [
          {
            path: 'login',
            element: (
              <TitledScreen
                title="AIEMD.ai Login"
                description="Sign in to your AIEMD.ai account"
                Component={LoginRegister}
              />
            ),
          },
          {
            path: 'registration',
            element: (
              <TitledScreen
                title="AIEMD.ai Registration"
                description="Create your AIEMD.ai account"
                Component={Registration}
              />
            ),
          },
        ],
      },

      {
        path: 'auth/reset-password',
        element: (
          <TitledScreen
            title="Reset Password"
            description="Reset your AIEMD.ai password"
            Component={ResetPassword}
          />
        ),
      },

      // Authenticated onboarding screens (authGuard).
      {
        element: <RequireAuth routePath="facial-recognition" />,
        children: [
          {
            path: 'facial-recognition',
            element: (
              <TitledScreen
                title="Facial Recognition Setup"
                description="Set up facial recognition for enhanced security"
                Component={FacialRecognition}
              />
            ),
          },
        ],
      },
      {
        element: <RequireAuth routePath="role-selection" />,
        children: [
          {
            path: 'role-selection',
            element: (
              <TitledScreen
                title="Role Selection"
                description="Select your professional role"
                Component={RoleSelection}
              />
            ),
          },
        ],
      },

      // Dashboard (authGuard + connectionGuard).
      {
        element: <RequireAuth routePath="dashboard" />,
        children: [
          {
            element: <RequireConnection />,
            children: [
              {
                path: 'dashboard',
                element: (
                  <TitledScreen
                    title="Dashboard"
                    description="Your personalized oncology workspace"
                    Component={Dashboard}
                  />
                ),
              },
            ],
          },
        ],
      },

      // Resilience routes.
      {
        path: 'service-outage',
        element: (
          <TitledScreen
            title="Service Status"
            description="System status and service information"
            Component={ServiceOutagePage}
          />
        ),
      },
      {
        path: 'dev/resilience-test',
        element: (
          <TitledScreen
            title="Resilience Test Page"
            description="Developer tool for testing resilience system"
            Component={ResilienceTestPage}
          />
        ),
      },

      // Simple error routes.
      {
        path: '404',
        element: (
          <Suspense fallback={<GuardLoading />}>
            <SimpleNotFound />
          </Suspense>
        ),
      },
      {
        path: '500',
        element: (
          <Suspense fallback={<GuardLoading />}>
            <SimpleServerError />
          </Suspense>
        ),
      },

      // Wildcard - must be last.
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);

export default router;
